import { useLocalStorage } from '@buffer-mono/popcorn'

import {
  LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
  DEFAULT_VIEW_MODE,
} from '~publish/components/Calendar/hooks/useCalendar'
import type { CalendarDateRangeViewMode } from '~publish/components/Calendar/hooks/useCalendarDateRangeViewMode'
import {
  LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
  DEFAULT_POST_LIST_VIEW_MODE,
} from '~publish/components/PostListOrCalendarViewToggle/PostListOrCalendarViewToggle'
import type { ListOrCalendarViewMode } from '~publish/components/PostListOrCalendarViewToggle/PostListOrCalendarViewToggle'

const VALID_VIEW_MODES: ReadonlySet<unknown> = new Set(['list', 'calendar'])
const VALID_GRANULARITIES: ReadonlySet<unknown> = new Set(['week', 'month'])

function isValidViewMode(value: unknown): value is ListOrCalendarViewMode {
  return VALID_VIEW_MODES.has(value)
}

function isValidGranularity(
  value: unknown,
): value is CalendarDateRangeViewMode {
  return VALID_GRANULARITIES.has(value)
}

type UseScheduleLastViewReturn = {
  /**
   * The calendar granularity preference ('week' or 'month')
   */
  calendarGranularity: CalendarDateRangeViewMode
  /**
   * Whether the user prefers the calendar view
   */
  prefersCalendar: boolean
}

/**
 * Hook that returns the user's schedule view preferences.
 *
 * This is used by sidebar navigation hooks to determine whether to link
 * to list or calendar views when navigating to schedule pages.
 *
 * @example
 * ```tsx
 * const { prefersCalendar, calendarGranularity } = useScheduleLastView()
 * const url = prefersCalendar
 *   ? `/schedule/calendar/${calendarGranularity}`
 *   : '/schedule'
 * ```
 */
export function useScheduleLastView(): UseScheduleLastViewReturn {
  const [rawViewMode] = useLocalStorage<ListOrCalendarViewMode>(
    LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_POST_LIST_VIEW_MODE,
  )

  const [rawCalendarGranularity] = useLocalStorage<CalendarDateRangeViewMode>(
    LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_VIEW_MODE,
  )

  // Validate values to guard against corrupt localStorage
  const viewMode = isValidViewMode(rawViewMode)
    ? rawViewMode
    : DEFAULT_POST_LIST_VIEW_MODE
  const calendarGranularity = isValidGranularity(rawCalendarGranularity)
    ? rawCalendarGranularity
    : DEFAULT_VIEW_MODE

  return {
    calendarGranularity,
    prefersCalendar: viewMode === 'calendar',
  }
}
